<template>
  <form @submit.prevent="$emit('submit')">
    <div class="w-full pb-24">
      <div
        v-if="showLocaleSwitcher"
        class="mb-6 flex items-center justify-between gap-4"
      >
        <span class="text-sm font-medium text-gray-700">Wersja językowa</span>
        <locale-editor-switch v-model="activeEditingLocale" />
      </div>

      <div class="space-y-6">
        <div>
          <h2 class="mb-3 px-1 text-sm font-semibold text-gray-900">
            Dane z MedfileAPI
          </h2>
          <div class="overflow-hidden rounded-2xl bg-white shadow-sm ring-1 ring-gray-950/5">
          <section class="px-6 py-8 sm:px-8">
            <div class="grid gap-6 lg:grid-cols-3 lg:gap-8">
              <div
                class="space-y-5"
                :class="syncedFieldsShellClass"
              >
                <text-input
                  v-model="form.specialist.first_name"
                  :errors="form.errors['specialist.first_name']"
                  class="w-full"
                  label="Imię"
                  required
                  maxlength="100"
                  :readonly="lockSyncedFields"
                />
                <text-input
                  v-model="form.specialist.last_name"
                  :errors="form.errors['specialist.last_name']"
                  class="w-full"
                  label="Nazwisko"
                  required
                  maxlength="100"
                  :readonly="lockSyncedFields"
                />
                <text-input
                  v-model="form.specialist.title"
                  :errors="form.errors['specialist.title']"
                  class="w-full"
                  label="Tytuł"
                  maxlength="255"
                  :readonly="lockSyncedFields"
                />
                <toggle-switch
                  v-if="showActiveToggle"
                  v-model="form.specialist.active"
                  :errors="form.errors['specialist.active'] || []"
                  class="w-full"
                  label="Aktywny"
                />
              </div>

              <div class="space-y-5">
                <file-input
                  v-model="form.specialist.photo"
                  :errors="form.errors['specialist.photo'] || form.errors.photo || []"
                  class="w-full"
                  label="Zdjęcie"
                  accept="image/jpeg,image/jpg,image/png,.jpg,.jpeg,.png"
                />
                <div v-if="photoUrl">
                  <p class="form-label mb-3">
                    Aktualne zdjęcie
                  </p>
                  <img
                    :src="photoUrl"
                    alt="Aktualne zdjęcie"
                    class="aspect-square h-24 w-24 rounded-xl object-cover ring-1 ring-gray-950/5"
                  >
                </div>
              </div>

              <div
                v-if="availableSpecialisations && availableSpecialisations.length > 0"
                class="space-y-5"
                :class="syncedFieldsShellClass"
              >
                <label class="form-label" :for="specialisationsSelectId">Specjalizacje</label>
                <div ref="specialisationsWrapper" class="admin-multiselect-wrapper mt-1">
                  <Multiselect
                    :id="specialisationsSelectId"
                    v-model="form.specialist.specialisation_ids"
                    class="admin-multiselect"
                    mode="tags"
                    :options="specialisationOptions"
                    value-prop="value"
                    label="label"
                    :searchable="!lockSyncedFields"
                    :filter-results="true"
                    :close-on-select="false"
                    :hide-selected="false"
                    :disabled="lockSyncedFields"
                    placeholder="Wybierz specjalizacje…"
                    no-options-text="Brak specjalizacji"
                    no-results-text="Brak wyników"
                    @open="positionDropdown"
                  >
                    <template #tag="{ option, handleTagRemove, disabled }">
                      <span
                        class="inline-flex items-center gap-1 rounded-full px-3 py-1 text-sm font-medium leading-tight"
                        :class="`${specialisationBadgeClasses(option)} mb-1 mr-1`"
                      >
                        {{ option.label }}
                        <button
                          v-if="!disabled"
                          type="button"
                          class="ml-0.5 rounded-full p-0 opacity-60 hover:opacity-100 focus:outline-none"
                          @mousedown.prevent="handleTagRemove(option, $event)"
                        >
                          ×
                        </button>
                      </span>
                    </template>
                    <template #option="{ option }">
                      <div class="flex w-full items-center justify-between gap-2 py-0.5">
                        <span class="font-medium">{{ option.label }}</span>
                        <site-context-badge
                          v-if="option.context_tag"
                          :tag="option.context_tag"
                          :color-token="option.context_color"
                        />
                      </div>
                    </template>
                  </Multiselect>
                </div>
                <div v-if="specialisationErrors.length" class="form-error">
                  {{ specialisationErrors[0] }}
                </div>
              </div>

              <div class="medfile-synced-fields opacity-60 lg:col-span-3">
                <textarea-input
                  :model-value="loc.remote_description || ''"
                  class="w-full"
                  :label="`Opis zdalny (${activeEditingLocale.toUpperCase()})`"
                  rows="6"
                  readonly
                  tabindex="-1"
                />
              </div>
            </div>
          </section>
          </div>
        </div>

        <div>
          <h2 class="mb-3 px-1 text-sm font-semibold text-gray-900">
            Dane lokalne
          </h2>
          <div class="overflow-hidden rounded-2xl bg-white shadow-sm ring-1 ring-gray-950/5">
          <div class="divide-y divide-gray-100">
            <section class="px-6 py-8 sm:px-8">
              <div class="mb-5 flex flex-wrap items-center justify-between gap-4">
                <div>
                  <h2 class="text-[11px] font-semibold uppercase tracking-wider text-gray-400">
                    Rejestracja online
                  </h2>
                  <p class="mt-1 text-xs text-gray-400">
                    Gdy włączone, pacjent musi opłacić wizytę przez PayU przed rejestracją w Medfile.
                    Domyślnie rejestracja idzie bezpośrednio do Medfile bez przedpłaty.
                  </p>
                </div>
                <toggle-switch
                  v-model="form.specialist.requires_prepayment"
                  :errors="form.errors['specialist.requires_prepayment'] || []"
                  description="Wymuś przedpłatę (PayU)"
                />
              </div>
            </section>
            <section class="px-6 py-8 sm:px-8">
              <div class="mb-5 flex flex-wrap items-center justify-between gap-4">
                <h2 class="text-[11px] font-semibold uppercase tracking-wider text-gray-400">
                  Opis lokalny ({{ activeEditingLocale.toUpperCase() }})
                </h2>
                <toggle-switch
                  v-model="form.specialist.use_local_description"
                  :errors="form.errors['specialist.use_local_description'] || []"
                  description="Używaj opisu lokalnego"
                />
              </div>
              <textarea-input
                v-model="loc.description"
                :errors="localeErrors('description')"
                class="w-full"
                :label="`Opis (${activeEditingLocale.toUpperCase()})`"
                rows="6"
                maxlength="5000"
              />
              <p class="mt-2 text-xs text-gray-400">
                Gdy przełącznik jest włączony, w profilu specjalisty wyświetlany jest ten opis
                (PL/EN). W przeciwnym razie pokazujemy nietłumaczony opis zdalny z karty
                „Dane podstawowe”.
              </p>
            </section>
            <section
              v-if="availableLanguages && availableLanguages.length > 0"
              class="px-6 py-8 sm:px-8"
            >
              <h2 class="mb-5 text-[11px] font-semibold uppercase tracking-wider text-gray-400">
                Języki
              </h2>
              <div ref="languagesWrapper" class="admin-multiselect-wrapper">
                <Multiselect
                  :id="languagesSelectId"
                  v-model="form.specialist.language_ids"
                  class="admin-multiselect"
                  mode="tags"
                  :options="languageOptions"
                  value-prop="value"
                  label="label"
                  :searchable="true"
                  :filter-results="true"
                  :close-on-select="false"
                  :hide-selected="false"
                  placeholder="Wybierz języki…"
                  no-options-text="Brak języków"
                  no-results-text="Brak wyników"
                  @open="positionLanguagesDropdown"
                >
                  <template #tag="{ option, handleTagRemove, disabled }">
                    <span class="mb-1 mr-1 inline-flex items-center gap-1 rounded-full bg-gray-100 px-3 py-1 text-sm font-medium leading-tight text-gray-800">
                      <img
                        v-if="option.image_url"
                        :src="option.image_url"
                        :alt="option.label"
                        class="h-3 w-4 rounded object-cover"
                      >
                      {{ option.label }}
                      <button
                        v-if="!disabled"
                        type="button"
                        class="ml-0.5 rounded-full p-0 opacity-60 hover:opacity-100 focus:outline-none"
                        @mousedown.prevent="handleTagRemove(option, $event)"
                      >
                        ×
                      </button>
                    </span>
                  </template>
                  <template #option="{ option }">
                    <div class="flex w-full items-center gap-2 py-0.5">
                      <img
                        v-if="option.image_url"
                        :src="option.image_url"
                        :alt="option.label"
                        class="h-3 w-4 rounded object-cover"
                      >
                      <span class="font-medium">{{ option.label }}</span>
                    </div>
                  </template>
                </Multiselect>
              </div>
              <div v-if="languageErrors.length" class="form-error">
                {{ languageErrors[0] }}
              </div>
            </section>
          <section class="px-6 py-8 sm:px-8">
            <h2 class="mb-5 text-[11px] font-semibold uppercase tracking-wider text-gray-400">
              Ważne informacje ({{ activeEditingLocale.toUpperCase() }})
            </h2>
            <div class="space-y-3">
              <div
                v-for="(item, index) in loc.important_information"
                :key="index"
                class="flex items-start gap-2"
              >
                <textarea
                  v-model="loc.important_information[index]"
                  rows="2"
                  class="form-input min-h-[72px] flex-1 rounded-lg"
                  placeholder="Wpisz informację…"
                />
                <button
                  type="button"
                  class="inline-flex shrink-0 items-center justify-center rounded-lg p-1.5 text-gray-400 transition hover:bg-red-50 hover:text-red-600"
                  title="Usuń"
                  @click="removeImportantInfo(index)"
                >
                  <icon name="trash" class="size-5 stroke-current" />
                </button>
              </div>
              <button
                type="button"
                class="inline-flex items-center gap-1.5 text-sm font-medium text-indigo-600 transition hover:text-indigo-700"
                @click="addImportantInfo"
              >
                <span aria-hidden="true" class="text-base leading-none">+</span>
                Dodaj informację
              </button>
            </div>
          </section>

          <section class="px-6 py-8 sm:px-8">
            <h2 class="mb-5 text-[11px] font-semibold uppercase tracking-wider text-gray-400">
              Leczone choroby ({{ activeEditingLocale.toUpperCase() }})
            </h2>
            <div class="space-y-3">
              <div
                v-for="(item, index) in loc.treated_diseases"
                :key="index"
                class="flex items-center gap-2"
              >
                <input
                  v-model="loc.treated_diseases[index]"
                  type="text"
                  class="form-input flex-1 rounded-lg"
                  placeholder="Wpisz chorobę…"
                >
                <button
                  type="button"
                  class="inline-flex shrink-0 items-center justify-center rounded-lg p-1.5 text-gray-400 transition hover:bg-red-50 hover:text-red-600"
                  title="Usuń"
                  @click="removeTreatedDisease(index)"
                >
                  <icon name="trash" class="size-5 stroke-current" />
                </button>
              </div>
              <button
                type="button"
                class="inline-flex items-center gap-1.5 text-sm font-medium text-indigo-600 transition hover:text-indigo-700"
                @click="addTreatedDisease"
              >
                <span aria-hidden="true" class="text-base leading-none">+</span>
                Dodaj chorobę
              </button>
            </div>
          </section>

          <section class="px-6 py-8 sm:px-8">
            <h2 class="mb-5 text-[11px] font-semibold uppercase tracking-wider text-gray-400">
              Media
            </h2>
            <div class="space-y-3">
              <div
                v-for="(item, index) in form.specialist.media"
                :key="index"
                class="flex items-center gap-2"
              >
                <input
                  v-model="form.specialist.media[index]"
                  type="url"
                  class="form-input flex-1 rounded-lg"
                  placeholder="https://…"
                >
                <button
                  type="button"
                  class="inline-flex shrink-0 items-center justify-center rounded-lg p-1.5 text-gray-400 transition hover:bg-red-50 hover:text-red-600"
                  title="Usuń"
                  @click="removeMedia(index)"
                >
                  <icon name="trash" class="size-5 stroke-current" />
                </button>
              </div>
              <button
                type="button"
                class="inline-flex items-center gap-1.5 text-sm font-medium text-indigo-600 transition hover:text-indigo-700"
                @click="addMedia"
              >
                <span aria-hidden="true" class="text-base leading-none">+</span>
                Dodaj link
              </button>
            </div>
          </section>

          <section class="px-6 py-8 sm:px-8">
            <h2 class="mb-5 text-[11px] font-semibold uppercase tracking-wider text-gray-400">
              FAQ ({{ activeEditingLocale.toUpperCase() }})
            </h2>
            <div class="space-y-5">
              <div
                v-for="(item, index) in loc.faq"
                :key="index"
                class="rounded-xl bg-gray-50/80 p-4 sm:p-5"
              >
                <div class="mb-4 flex items-center justify-between gap-2">
                  <span class="text-xs font-semibold uppercase tracking-wider text-gray-400">
                    Pytanie {{ index + 1 }}
                  </span>
                  <button
                    type="button"
                    class="inline-flex shrink-0 items-center justify-center rounded-lg p-1.5 text-gray-400 transition hover:bg-red-50 hover:text-red-600"
                    title="Usuń"
                    @click="removeFaqItem(index)"
                  >
                    <icon name="trash" class="size-5 stroke-current" />
                  </button>
                </div>
                <text-input
                  v-model="item.question"
                  class="w-full pb-4"
                  label="Pytanie"
                  maxlength="500"
                />
                <textarea-input
                  v-model="item.answer"
                  class="w-full"
                  label="Odpowiedź"
                  rows="3"
                  maxlength="5000"
                />
              </div>
              <button
                type="button"
                class="inline-flex items-center gap-1.5 text-sm font-medium text-indigo-600 transition hover:text-indigo-700"
                @click="addFaqItem"
              >
                <span aria-hidden="true" class="text-base leading-none">+</span>
                Dodaj pytanie
              </button>
            </div>
          </section>
          </div>
          </div>
        </div>
      </div>
    </div>
    <slot />
  </form>
</template>

<script>
import TextInput from '@/Shared/TextInput.vue';
import TextareaInput from '@/Shared/TextareaInput.vue';
import FileInput from '@/Shared/FileInput.vue';
import ToggleSwitch from '@/Shared/ToggleSwitch.vue';
import SiteContextBadge from '@/Shared/SiteContextBadge.vue';
import LocaleEditorSwitch from '@/Shared/LocaleEditorSwitch.vue';
import Icon from '@/Shared/Icon.vue';
import Multiselect from '@vueform/multiselect';
import { badgeClassesForContextColor } from '@/utils/siteContextBadge';

export default {
  components: {
    TextInput,
    TextareaInput,
    FileInput,
    ToggleSwitch,
    SiteContextBadge,
    LocaleEditorSwitch,
    Icon,
    Multiselect,
  },
  props: {
    modelValue: {
      type: Object,
      required: true,
    },
    photoUrl: {
      type: String,
      default: null,
    },
    availableSpecialisations: {
      type: Array,
      default: () => [],
    },
    availableLanguages: {
      type: Array,
      default: () => [],
    },
    editingLocale: {
      type: String,
      default: undefined,
    },
    showLocaleSwitcher: {
      type: Boolean,
      default: true,
    },
    showActiveToggle: {
      type: Boolean,
      default: true,
    },
    lockSyncedFields: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['update:modelValue', 'update:editingLocale', 'submit'],
  data() {
    return {
      specialisationsSelectId: `specialist-specialisations-${Math.random().toString(36).slice(2, 9)}`,
      languagesSelectId: `specialist-languages-${Math.random().toString(36).slice(2, 9)}`,
      internalEditingLocale: 'pl',
    };
  },
  computed: {
    form: {
      get() {
        return this.modelValue;
      },
      set(val) {
        this.$emit('update:modelValue', val);
      },
    },
    activeEditingLocale: {
      get() {
        return this.editingLocale !== undefined ? this.editingLocale : this.internalEditingLocale;
      },
      set(val) {
        if (this.editingLocale !== undefined) {
          this.$emit('update:editingLocale', val);
        } else {
          this.internalEditingLocale = val;
        }
      },
    },
    loc() {
      const slice = this.form.specialist[`data_${this.activeEditingLocale}`];
      if (!Array.isArray(slice.important_information)) {
        slice.important_information = [];
      }
      if (!Array.isArray(slice.treated_diseases)) {
        slice.treated_diseases = [];
      }
      if (!Array.isArray(slice.faq)) {
        slice.faq = [];
      }
      return slice;
    },
    specialisationErrors() {
      const errors = this.form.errors || {};
      return (
        errors['specialist.specialisation_ids'] ||
        errors.specialisation_ids ||
        []
      );
    },
    languageErrors() {
      const errors = this.form.errors || {};
      return (
        errors['specialist.language_ids'] ||
        errors.language_ids ||
        []
      );
    },
    specialisationOptions() {
      return (this.availableSpecialisations || []).map((specialisation) => ({
        value: specialisation.id,
        label: specialisation.label || specialisation.name,
        context_tag: specialisation.context_tag,
        context_color: specialisation.context_color,
      }));
    },
    languageOptions() {
      return (this.availableLanguages || []).map((language) => ({
        value: language.id,
        label: language.label || language.name,
        image_url: language.image_url,
      }));
    },
    syncedFieldsShellClass() {
      if (!this.lockSyncedFields) {
        return null;
      }

      return 'medfile-synced-fields opacity-60';
    },
  },
  created() {
    if (typeof this.form.specialist.use_local_description !== 'boolean') {
      this.form.specialist.use_local_description = Boolean(this.form.specialist.use_local_description);
    }
    if (typeof this.form.specialist.requires_prepayment !== 'boolean') {
      this.form.specialist.requires_prepayment = Boolean(this.form.specialist.requires_prepayment);
    }
    if (!Array.isArray(this.form.specialist.media)) {
      this.form.specialist.media = [];
    }
    if (!Array.isArray(this.form.specialist.specialisation_ids)) {
      this.form.specialist.specialisation_ids = [];
    }
    if (!Array.isArray(this.form.specialist.language_ids)) {
      this.form.specialist.language_ids = [];
    }
    ['data_pl', 'data_en'].forEach((key) => {
      if (!this.form.specialist[key]) {
        this.form.specialist[key] = { description: '', remote_description: '' };
      }
      const slice = this.form.specialist[key];
      if (typeof slice.description !== 'string') {
        slice.description = '';
      }
      if (typeof slice.remote_description !== 'string') {
        slice.remote_description = slice.remote_description || '';
      }
      if (!Array.isArray(slice.important_information)) {
        slice.important_information = [];
      }
      if (!Array.isArray(slice.treated_diseases)) {
        slice.treated_diseases = [];
      }
      if (!Array.isArray(slice.faq)) {
        slice.faq = [];
      }
    });
  },
  methods: {
    localeErrors(field) {
      const errors = this.form.errors || {};
      const raw =
        errors[`specialist.data_${this.activeEditingLocale}.${field}`] ||
        errors[`specialist.${field}`] ||
        errors[field];
      if (!raw) return [];
      return Array.isArray(raw) ? raw : [raw];
    },
    specialisationBadgeClasses(option) {
      return badgeClassesForContextColor(option.context_color || 'code-medical-center');
    },
    positionDropdown() {
      this.$nextTick(() => {
        const wrapper = this.$refs.specialisationsWrapper;
        if (!wrapper) return;
        const rect = wrapper.getBoundingClientRect();
        wrapper.style.setProperty('--ms-fixed-top', `${rect.bottom + 2}px`);
        wrapper.style.setProperty('--ms-fixed-left', `${rect.left}px`);
        wrapper.style.setProperty('--ms-fixed-width', `${rect.width}px`);
      });
    },
    positionLanguagesDropdown() {
      this.$nextTick(() => {
        const wrapper = this.$refs.languagesWrapper;
        if (!wrapper) return;
        const rect = wrapper.getBoundingClientRect();
        wrapper.style.setProperty('--ms-fixed-top', `${rect.bottom + 2}px`);
        wrapper.style.setProperty('--ms-fixed-left', `${rect.left}px`);
        wrapper.style.setProperty('--ms-fixed-width', `${rect.width}px`);
      });
    },
    addImportantInfo() {
      this.loc.important_information.push('');
    },
    removeImportantInfo(index) {
      this.loc.important_information.splice(index, 1);
    },
    addMedia() {
      if (!Array.isArray(this.form.specialist.media)) {
        this.form.specialist.media = [];
      }
      this.form.specialist.media.push('');
    },
    removeMedia(index) {
      this.form.specialist.media.splice(index, 1);
    },
    addTreatedDisease() {
      this.loc.treated_diseases.push('');
    },
    removeTreatedDisease(index) {
      this.loc.treated_diseases.splice(index, 1);
    },
    addFaqItem() {
      this.loc.faq.push({ question: '', answer: '' });
    },
    removeFaqItem(index) {
      this.loc.faq.splice(index, 1);
    },
  },
};
</script>

<style scoped>
.medfile-synced-fields :deep(.form-label) {
  color: var(--color-green-700);
}

.medfile-synced-fields :deep(.form-input),
.medfile-synced-fields :deep(.form-textarea) {
  cursor: default;
  border-color: var(--color-green-200);
  background-color: var(--color-green-50);
  color: var(--color-green-800);
}

.medfile-synced-fields :deep(.form-input:focus),
.medfile-synced-fields :deep(.form-textarea:focus) {
  border-color: var(--color-green-300);
  outline: none;
  box-shadow: none;
}

.medfile-synced-fields :deep(.admin-multiselect) {
  cursor: default;
  border-color: var(--color-green-200);
  background-color: var(--color-green-50);
}

.medfile-synced-fields :deep(.admin-multiselect.is-disabled) {
  opacity: 1;
}
</style>
